/**
 * Fold a model's FINE voxels onto a coarser lattice — the whole model, hollow interiors,
 * carved openings and decorations included.
 *
 * WHY A FOLD RATHER THAN A STAND-IN, at the levels where it is used. A stand-in re-authors the model
 * out of primitives, which is the only option for a tree (its trunk is thinner than a lod-1 voxel, so
 * anything derived from the real thing loses it) and is a poor deal for a building at lod 1: a lod-1
 * voxel is 0.5 m and LOD 0's walls are ALREADY 0.5 m thick, so the real model transfers essentially
 * intact. Every fine sample is still visited here — they are COMBINED, not decimated, which is what
 * separates this from sampling the model at half resolution and getting broken shells.
 *
 * THE RULE IS "STRONGEST WEIGHT WINS THE CELL", and its direction is the load-bearing choice. A cell
 * whose eight samples are all carve stays carve — that is the hollow interior, and the door and window
 * openings. A cell mixing wall and carve goes SOLID, so openings shrink by up to one coarse voxel per
 * edge and walls never erode. The opposite rule would dissolve every 2-fine-voxel wall in the world.
 * Measured against the models: a window is 6x4 fine voxels (3x2 coarse cells, surviving even
 * misaligned), a door 6x8, a crenel gap ~2.4 cells of arc, and interior floors and the tower's spiral
 * ramp are 2-2.5 fine voxels, i.e. one coarse voxel each.
 *
 * Ties go to the EMISSIVE material first (a torch flame beside a wall is the more important of the two
 * to keep) and then to the later voxel, which is the order LOD 0's own blend resolves them in.
 */
import { MATERIAL_EMISSION_LUT } from '../materials/Materials.js';
/** Floor division that stays correct for negative coordinates (a model straddles its origin). */
function cellOf(v, stride) {
    return Math.floor(v / stride);
}
export function foldVoxels(fine, stride) {
    if (stride <= 1)
        return fine;
    if (fine.length === 0)
        return [];
    // DENSE, NOT A MAP. The model's coarse bounding box is known up front, so the cell state lives in
    // three typed arrays indexed by cell rather than a Map of per-cell objects: no hashing, no
    // allocation per cell, and the same "strongest wins, emissive breaks ties, later wins otherwise"
    // rule. Measured per placed building at lod 1: 12 ms with the Map, ~2 ms dense. Output order is
    // the Map's — first-touch order of the cells — recorded as the cells are first seen, so the folded
    // list is identical entry for entry.
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const v of fine) {
        if (v.x < minX)
            minX = v.x;
        if (v.x > maxX)
            maxX = v.x;
        if (v.y < minY)
            minY = v.y;
        if (v.y > maxY)
            maxY = v.y;
        if (v.z < minZ)
            minZ = v.z;
        if (v.z > maxZ)
            maxZ = v.z;
    }
    const cx0 = cellOf(minX, stride), cy0 = cellOf(minY, stride), cz0 = cellOf(minZ, stride);
    const nx = cellOf(maxX, stride) - cx0 + 1, ny = cellOf(maxY, stride) - cy0 + 1, nz = cellOf(maxZ, stride) - cz0 + 1;
    const cells = nx * ny * nz;
    const weight = new Float64Array(cells);
    const material = new Int32Array(cells);
    const seen = new Uint8Array(cells);
    const order = new Int32Array(cells);
    let nSeen = 0;
    for (const v of fine) {
        const c = (cellOf(v.z, stride) - cz0) * ny * nx + (cellOf(v.y, stride) - cy0) * nx + (cellOf(v.x, stride) - cx0);
        if (seen[c]) {
            if (v.weight < weight[c])
                continue;
            if (v.weight === weight[c]
                && MATERIAL_EMISSION_LUT[v.material] < MATERIAL_EMISSION_LUT[material[c]])
                continue;
        }
        else {
            seen[c] = 1;
            order[nSeen++] = c;
        }
        weight[c] = v.weight;
        material[c] = v.material;
    }
    const out = new Array(nSeen);
    for (let k = 0; k < nSeen; k++) {
        const c = order[k];
        const x = c % nx, y = Math.floor(c / nx) % ny, z = Math.floor(c / (nx * ny));
        out[k] = { x: x + cx0, y: y + cy0, z: z + cz0, material: material[c], weight: weight[c] };
    }
    return out;
}
//# sourceMappingURL=fold.js.map